import React, { useState, useRef, useEffect } from 'react';
import { 
  Search, 
  // MessageSquare removed (unused)
  Menu, 
  X,
  Info,
  PhoneCall,
  ArrowRight,
  Home as HomeIcon,
  Briefcase,
  Cpu,
  Trophy,
  // UsersIcon removed (unused)
  UserPlus,
  Mail as MailIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  const navItems = [
    { name: 'Home', path: '/', icon: HomeIcon },
    { name: 'About us', path: '/about', icon: Info },
    { name: 'Services', path: '/services', icon: Cpu },
    { name: 'Careers', path: '/careers', icon: Briefcase },
    { name: 'Contact', path: '/contact', icon: MailIcon },
    { name: 'Portfolio', path: '/portfolio', icon: Trophy },
    { name: 'Join us', path: '/join-us', icon: UserPlus }
  ];

  const searchableItems = [
    ...navItems,
    { name: 'Web Development', path: '/services/web', keywords: ['web', 'website', 'frontend', 'backend', 'fullstack'] },
    { name: 'Mobile Development', path: '/services/mobile', keywords: ['mobile', 'app', 'android', 'ios', 'iphone'] },
    { name: 'Digital Marketing', path: '/services/digital-marketing', keywords: ['marketing', 'seo', 'ads', 'social media'] },
    { name: 'Cloud Solutions', path: '/services/cloud', keywords: ['cloud', 'aws', 'azure', 'google cloud', 'hosting'] },
    { name: 'Maintenance & Support', path: '/services/maintenance', keywords: ['maintenance', 'support', 'help', 'update'] },
    { name: 'Security Solutions', path: '/services/security', keywords: ['security', 'hacking', 'protection', 'cyber'] },
    { name: 'Domain', path: '/services/web', keywords: ['domain', 'name'] },
  ];

  const suggestions = searchQuery.trim() 
    ? searchableItems.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (item as any).keywords?.some((k: string) => k.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 5)
    : [];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target as Node)) {
        // We don't necessarily hide mobile suggestions on click outside if the menu is open
        // but it's good for consistency if needed.
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent | React.KeyboardEvent) => {
    if ('key' in e && e.key !== 'Enter') return;
    e.preventDefault();

    if (!searchQuery.trim()) return;

    const query = searchQuery.toLowerCase().trim();
    
    const result = searchableItems.find(item => 
      item.name.toLowerCase().includes(query) || 
      (item as any).keywords?.some((k: string) => k.toLowerCase().includes(query))
    );

    if (result) {
      navigate(result.path);
      setSearchQuery('');
      setShowSuggestions(false);
      setIsMenuOpen(false);
    } else {
      toast.error(`No results found for "${searchQuery}". Try searching for 'web', 'mobile', or 'cloud'.`, {
        style: {
          borderRadius: '10px',
          background: '#002a5c',
          color: '#fff',
        },
      });
    }
  };

  const handleSuggestionClick = (path: string) => {
    navigate(path);
    setSearchQuery('');
    setShowSuggestions(false);
    setIsMenuOpen(false);
  };

  const activeTab = navItems.find(item => item.path === location.pathname)?.name || 'Home';

  return (
    <header className="bg-[#002a5c] text-white sticky top-0 z-50">
      {/* Top Bar */}
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 md:gap-4 shrink-0">
          <div className="animate__animated animate__jackInTheBox">
            <img src="/logo.png" alt="Bank Logo" className="h-10 sm:h-14 md:h-16 w-auto" />
          </div>
          <div className="hidden xs:block h-6 w-[1px] bg-white/20 mx-1 md:mx-2" />
          <span className="hidden sm:block lg:block text-[10px] md:text-sm font-light italic text-[#ffcc00] animate-text-glow animate__animated animate__flash animate__infinite animate__slower mr-2 whitespace-nowrap">Your's Digital Ally, Always!</span>
        </Link>
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleTheme();
          }}
          className={`w-8 h-4 md:w-9 md:h-[18px] rounded-full transition-colors relative shrink-0 ${isDark ? 'bg-blue-600' : 'bg-slate-400/40'}`}
        >
          <div 
            className={`absolute top-[2px] w-2.5 h-2.5 md:w-3.5 md:h-3.5 bg-white rounded-full transition-all ${isDark ? 'left-[18px] md:left-[20px]' : 'left-[2px]'}`}
          />
        </button>

        <div className="flex flex-1 max-w-xl px-2 md:px-4 lg:px-8">
          <div className="relative w-full" ref={searchRef}>
            <form onSubmit={handleSearch} className="relative w-full">
              <Search 
                className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer hover:text-white transition-colors" 
                size={14} 
                onClick={handleSearch}
              />
              <input 
                type="text" 
                value={searchQuery}
                onFocus={() => setShowSuggestions(true)}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }}
                placeholder="Search.."
                className="w-full bg-white text-slate-800 h-8 md:h-10 rounded-md pl-8 md:pl-10 pr-2 md:pr-4 text-[10px] md:text-sm focus:outline-none"
              />
            </form>

            {/* Suggestions Dropdown */}
            <AnimatePresence>
              {showSuggestions && suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden z-[60]"
                >
                  <div className="p-2">
                    {suggestions.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(item.path)}
                        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors group text-left"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-[#002a5c]">{item.name}</span>
                          <span className="text-[10px] text-slate-400">{item.path}</span>
                        </div>
                        <ArrowRight size={14} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex items-center gap-3 md:gap-6">
          <button className="hidden sm:flex items-center gap-2 text-sm hover:text-blue-200 transition-colors shrink-0">
            <PhoneCall size={18} /> <span className="hidden xl:inline">9849 231 222</span>
          </button>
          <div className="flex items-center gap-3 md:gap-4">
            {/* <PhoneCall size={18} className="cursor-pointer hover:text-blue-200 md:w-5 md:h-5" /> */}
            {/* <MessageSquare size={18} className="cursor-pointer hover:text-green-400 md:w-5 md:h-5" /> */}
            <div className="flex items-center gap-2 bg-white/10 p-1 md:px-3 md:py-1.5 rounded-full border border-white/20">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-1">
                {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
              <div className="w-6 h-6 md:w-8 md:h-8 bg-white text-[#002a5c] rounded-full flex items-center justify-center font-semibold text-[10px] md:text-xs">
                OV
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs (Desktop Only) */}
      <div className="hidden md:block bg-[#003c7a] border-t border-white/10 overflow-x-auto scrollbar-hide">
        <div className="max-w-7xl mx-auto px-4 flex">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              className={`px-4 lg:px-6 py-4 text-[10px] lg:text-xs font-inter font-semibold transition-all relative whitespace-nowrap flex items-center gap-2 ${
                activeTab === item.name ? 'text-[#ffcc00]' : 'text-slate-300 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={16} className={`${activeTab === item.name ? 'text-[#ffcc00]' : 'text-slate-400'} transition-colors`} />
              {item.name}
              {activeTab === item.name && (
                <motion.div 
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-1 bg-[#ffcc00] shadow-[0_-4px_10px_rgba(255,204,0,0.3)]" 
                />
              )}
            </Link>
          ))}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/50 md:hidden z-40"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-3/4 max-w-xs bg-[#002a5c] z-50 md:hidden p-6 shadow-2xl"
            >
              <div className="flex justify-between items-center mb-8">
                <div>
                  <img src="/logo.png" alt="Logo" className="h-14" />
                </div>
                <button onClick={() => setIsMenuOpen(false)} className="p-2 text-white/60">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="space-y-1">
                  {navItems.map((item) => (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-sm font-medium transition-all ${
                        activeTab === item.name 
                        ? 'bg-white text-[#002a5c]' 
                        : 'text-slate-300 hover:bg-white/5'
                      }`}
                    >
                      <item.icon size={18} className={activeTab === item.name ? 'text-[#002a5c]' : 'text-slate-400'} />
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
